import { EnvironmentService } from '../../../environment/environment-service';
import {
  DashboardConfig,
  DashboardId,
} from '../../common/components/dashboards/types';
import { Domains } from '../../constants';
import {
  creaturesDashboardConfigs,
  defaultDashboardConfigs,
  jtbDashboardConfigs,
  jtDashboardConfigs,
  mixiDashboardConfigs,
  nikkiDashboardConfigs,
  patagoniaDashboardConfigs,
  shipHealthcareDashboardConfigs,
  swanDashboardConfigs,
  washiDashboardConfigs,
} from '../../settings/dashboard-configs/dashboard-configs-json';
import { Hasher } from '../hasher';
import {
  DomainPreferences,
  DomainSettings,
  HasDependencies,
  hashString,
} from '../types';
import { DashboardNotFoundError } from './dashboard-errors';
import { DashboardService } from './dashboard-service';
import { FullViewDashboardService } from './full-view-dashboard-service';

export class DomainViewDashboardService
  implements DashboardService, HasDependencies
{
  constructor(
    private readonly fullViewDashboardService: FullViewDashboardService,
    private readonly latestPreferences: DomainPreferences,
    private readonly latestSettings: DomainSettings,
    private readonly domain: Domains,
    private readonly environmentService: EnvironmentService
  ) {}

  public getDeps = () => [
    hashString([
      ...this.fullViewDashboardService.getDeps(),
      Hasher.hash(this.latestPreferences),
      Hasher.hash(this.latestSettings),
      this.domain,
    ]),
  ];

  public listDashboards = async () => {
    const companyAllowedDashboards: string[] =
      this.latestSettings.allowedDashboards?.map((d) => d.id) ?? [];
    const allDashboards = await this.fullViewDashboardService.listDashboards();
    return allDashboards.filter((d) => companyAllowedDashboards.includes(d.id));
  };

  public listCustomDashboardConfigs = async () => {
    const onlyQaDomains: Domains[] = [];
    const isOnlyQaDomain = onlyQaDomains.includes(this.domain);
    if (this.environmentService.isProd && isOnlyQaDomain) {
      return JSON.parse(JSON.stringify([])) as DashboardConfig[];
    }
    let dashboardConfigs: DashboardConfig[];
    switch (this.domain) {
      case Domains.SWAN:
        dashboardConfigs = swanDashboardConfigs;
        break;
      case Domains.JT:
        dashboardConfigs = jtDashboardConfigs;
        break;
      case Domains.CREATURES:
        dashboardConfigs = creaturesDashboardConfigs;
        break;
      case Domains.JTB:
        dashboardConfigs = jtbDashboardConfigs;
        break;
      case Domains.SHIP_HEALTHCARE:
        dashboardConfigs = shipHealthcareDashboardConfigs;
        break;
      case Domains.NIKKI:
        dashboardConfigs = nikkiDashboardConfigs;
        break;
      case Domains.MIXI_COMPANY_WIDE:
        dashboardConfigs = mixiDashboardConfigs;
        break;
      case Domains.WASHI:
        dashboardConfigs = washiDashboardConfigs;
        break;
      case Domains.PATAGONIA:
        dashboardConfigs = patagoniaDashboardConfigs;
        break;
      default:
        dashboardConfigs = defaultDashboardConfigs;
        break;
    }
    return dashboardConfigs;
  };

  public getCustomDashboardConfig = async (dashboardId: DashboardId) => {
    const dashboardConfigs = await this.listCustomDashboardConfigs();
    const dashboardConfig =
      dashboardConfigs.find((dc) => dc.id === dashboardId) ?? null;
    if (dashboardConfig === null) {
      return new DashboardNotFoundError(dashboardId);
    } else {
      return dashboardConfig;
    }
  };
}
